import styled, { keyframes } from "styled-components";
import { lg, sm } from "../../../BreakPoints";

const fadeRise = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

export const Container = styled.main`
  width: 100%;
  min-height: 100vh;
  padding: 4rem 1.2rem 1.9rem;
  background: radial-gradient(circle at 7% 8%, rgba(59, 99, 247, 0.18), transparent 24%),
    radial-gradient(circle at 90% 15%, rgba(46, 196, 182, 0.16), transparent 22%),
    linear-gradient(180deg, var(--ice-blue) 0%, var(--surface-soft) 38%, var(--surface-white) 100%);
`;

export const Content = styled.div`
  max-width: 1140px;
  margin: 0 auto;
  display: grid;
  gap: 2.6rem;
`;

export const HeroSection = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
  align-items: center;
  gap: 1.7rem;

  ${lg({
    gridTemplateColumns: "1fr",
    gap: "1.3rem",
  })}
`;

export const LeftSide = styled.div`
  animation: ${fadeRise} 0.65s ease-out;
`;

export const LeftWrapper = styled.div`
  max-width: 34rem;
`;

export const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid rgba(38, 92, 214, 0.2);
  color: var(--brand-blue);
  background: rgba(255, 255, 255, 0.7);
  padding: 0.42rem 0.8rem;
  font-size: 0.77rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const Title = styled.h1`
  margin-top: 1rem;
  color: var(--deep-navy);
  font-size: clamp(2.2rem, 5.2vw, 3.35rem);
  line-height: 1.1;
`;

export const Text = styled.p`
  margin-top: 1rem;
  color: var(--text-muted);
  font-size: 1.04rem;
  line-height: 1.7;
`;

export const Actions = styled.div`
  margin-top: 1.6rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

export const Button = styled.button`
  border: none;
  border-radius: 0.82rem;
  font-family: var(--font-display);
  font-size: 0.92rem;
  font-weight: 700;
  padding: 0.82rem 1.22rem;
  cursor: pointer;
  color: ${({ secondary }) => (secondary ? "var(--brand-blue)" : "var(--surface-white)")};
  background: ${({ secondary }) =>
    secondary ? "rgba(255, 255, 255, 0.8)" : "var(--brand-blue)"};
  border: ${({ secondary }) => (secondary ? "1px solid var(--line-mid)" : "none")};
  box-shadow: ${({ secondary }) =>
    secondary ? "none" : "0 14px 26px rgba(25, 85, 217, 0.28)"};
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${({ secondary }) =>
      secondary ? "none" : "0 16px 30px rgba(25, 85, 217, 0.35)"};
    background: ${({ secondary }) =>
      secondary ? "var(--surface-white)" : "var(--brand-blue-strong)"};
  }

  ${sm({
    width: "100%",
  })}
`;

export const Meta = styled.ul`
  margin: 1.4rem 0 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 0.5rem;
`;

export const MetaItem = styled.li`
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--deep-navy);
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: "";
    width: 0.44rem;
    height: 0.44rem;
    border-radius: 999px;
    background: var(--brand-blue);
    box-shadow: 0 0 0 5px rgba(42, 115, 255, 0.15);
  }
`;

export const ProductHuntRow = styled.div`
  margin-top: 1.4rem;
`;

export const RightSide = styled.div`
  animation: ${scaleIn} 0.65s ease-out 0.1s both;
  display: flex;
  justify-content: center;
`;

export const VisualPanel = styled.div`
  position: relative;
  width: min(34rem, 100%);
  border-radius: 1.2rem;
  padding: 1rem;
  background: linear-gradient(165deg, rgba(255, 255, 255, 0.92), rgba(237, 247, 255, 0.9));
  border: 1px solid rgba(255, 255, 255, 0.95);
  box-shadow: 0 30px 45px rgba(25, 60, 125, 0.2);
`;

export const SvgItem = styled.img`
  width: 100%;
  height: auto;
  border-radius: 0.9rem;
  object-fit: cover;
`;

export const FloatingNote = styled.div`
  position: absolute;
  right: 1.5rem;
  bottom: 1.35rem;
  border-radius: 0.85rem;
  background-color: rgba(26, 35, 64, 0.9);
  color: var(--surface-white);
  padding: 0.65rem 0.75rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.02em;
`;

export const FeatureSection = styled.section`
  display: grid;
  gap: 1.2rem;
`;

export const SectionTitle = styled.h2`
  font-size: clamp(1.5rem, 3.5vw, 2rem);
  color: var(--deep-navy);
`;

export const SectionText = styled.p`
  max-width: 48rem;
  color: var(--text-muted);
  font-size: 1rem;
  line-height: 1.65;
`;

export const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.9rem;

  ${lg({
    gridTemplateColumns: "1fr",
  })}
`;

export const Feature = styled.article`
  background: rgba(255, 255, 255, 0.74);
  border: 1px solid rgba(29, 77, 182, 0.12);
  border-radius: 0.9rem;
  padding: 1.05rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 18px 30px rgba(20, 67, 161, 0.14);
  }
`;

export const FeatureTitle = styled.h3`
  font-size: 1rem;
  color: var(--deep-navy);
`;

export const FeatureText = styled.p`
  margin-top: 0.5rem;
  color: var(--text-muted);
  font-size: 0.92rem;
  line-height: 1.55;
`;

export const Workflow = styled.section`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  background: linear-gradient(120deg, var(--ice-blue), var(--surface-soft));
  border: 1px solid rgba(54, 113, 227, 0.16);
  border-radius: 1rem;
  padding: 1rem;

  ${lg({
    gridTemplateColumns: "1fr",
  })}
`;

export const Step = styled.div`
  position: relative;
  padding-left: 1.2rem;

  &::before {
    content: "";
    position: absolute;
    top: 0.26rem;
    left: 0;
    width: 0.45rem;
    height: 0.45rem;
    border-radius: 999px;
    background-color: var(--brand-blue);
  }
`;

export const StepTitle = styled.h4`
  font-size: 0.9rem;
  color: var(--deep-navy);
`;

export const StepText = styled.p`
  margin-top: 0.45rem;
  font-size: 0.84rem;
  line-height: 1.5;
  color: var(--text-muted);
`;

export const CtaSection = styled.section`
  border-radius: 1.1rem;
  padding: 1.3rem 1.2rem;
  background: linear-gradient(130deg, var(--deep-navy), var(--deep-navy-soft));
  color: var(--surface-white);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  ${lg({
    flexDirection: "column",
    alignItems: "flex-start",
  })}
`;

export const CtaTitle = styled.h3`
  font-size: clamp(1.1rem, 3vw, 1.45rem);
  color: var(--surface-white);
  line-height: 1.25;
`;

export const CtaText = styled.p`
  margin-top: 0.35rem;
  color: rgba(242, 246, 255, 0.95);
  font-size: 0.92rem;
`;

export const CtaAction = styled(Button)`
  background: var(--surface-white);
  color: var(--brand-blue);
  box-shadow: none;
  white-space: nowrap;

  &:hover {
    background: var(--ice-blue);
    box-shadow: none;
  }
`;

export const ReviewsSection = styled.section`
  display: grid;
  gap: 0.9rem;
`;

export const ReviewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.8rem;

  ${lg({
    gridTemplateColumns: "1fr",
  })}
`;

export const ReviewCard = styled.article`
  border-radius: 0.9rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(29, 77, 182, 0.12);
  box-shadow: 0 18px 30px rgba(20, 67, 161, 0.09);
  display: grid;
  gap: 0.45rem;
`;

export const ReviewStars = styled.p`
  font-size: 0.9rem;
  color: var(--coral);
`;

export const ReviewText = styled.p`
  color: var(--text-muted);
  font-size: 0.86rem;
  line-height: 1.5;
`;

export const ReviewAuthor = styled.p`
  color: var(--deep-navy);
  font-size: 0.82rem;
  font-weight: 700;
`;

export const Footer = styled.footer`
  border-top: 1px solid rgba(26, 71, 170, 0.12);
  padding-top: 1rem;
  margin-top: 0.5rem;
`;

export const FooterInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.7rem;

  ${sm({
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "0.5rem",
  })}
`;

export const FooterText = styled.p`
  color: var(--text-muted);
  font-size: 0.8rem;
`;

export const FooterLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

export const FooterLink = styled.button`
  border: none;
  background: transparent;
  color: var(--brand-blue);
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0;
  cursor: pointer;

  &:hover {
    color: var(--brand-blue-strong);
  }
`;
